"use client";
import { useEffect, useRef, useState, useCallback } from "react";
import WishlistDetailPanel from "./WishlistDetailPanel";
import MapFilterPanel from "./MapFilterPanel";
import ClusterPanel from "./ClusterPanel";
import * as maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import "maplibre-gl/dist/maplibre-gl.css";
import { svgStr, svgMultiStr, loadSVGImage} from './schoolPoint';

function debounce(fn, delay) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
}

export default function DemographicsMap({ initialSchoolID = null }) {
  const mapRef   = useRef(null);
  const mapObj   = useRef(null);
  const popupRef = useRef(null);

  const [wishlistCount,    setWishlistCount]    = useState(0);
  const [totalCount,       setTotalCount]       = useState(0);
  const [filters,          setFilters]          = useState({});
  const [detailSchoolID,   setDetailSchoolID]   = useState(null);
  const [clusterSchoolIDs, setClusterSchoolIDs] = useState(null);
  const [singleSchoolID,   setSingleSchoolID]   = useState(null);
  const [filterOpen,       setFilterOpen]       = useState(false);

  const wishlistDataRef = useRef(null);
  const setClusterRef   = useRef(setClusterSchoolIDs);
  const setSingleRef    = useRef(setSingleSchoolID);
  const filtersRef      = useRef(filters);

  // 🌟 Keep references dynamic on every state cycle change to avoid stale state bugs
  useEffect(() => { filtersRef.current    = filters;             });
  useEffect(() => { setClusterRef.current = setClusterSchoolIDs; });
  useEffect(() => { setSingleRef.current  = setSingleSchoolID;   });

  // Helper method: Groups raw features by exact coordinate before rendering
  const groupDataByCoordinates = (geoJson) => {
    if (!geoJson || !geoJson.features) return geoJson;

    const coordMap = {};
    geoJson.features.forEach(f => {
      if (!f.geometry?.coordinates) return;
      const key = `${f.geometry.coordinates[0].toFixed(5)},${f.geometry.coordinates[1].toFixed(5)}`;
      if (!coordMap[key]) {
        coordMap[key] = [];
      }
      coordMap[key].push(f);
    });

    const combinedFeatures = Object.values(coordMap).map(featuresAtCoord => {
      const baseFeature = JSON.parse(JSON.stringify(featuresAtCoord[0]));

      // If there's more than 1 teacher at this school, merge their IDs into a stringified array
      if (featuresAtCoord.length > 1) {
        const schoolIDsArray = featuresAtCoord.map(f => f.properties?.schoolID).filter(Boolean);
        baseFeature.properties.allSchoolIDs = JSON.stringify(schoolIDsArray);
        baseFeature.properties.isMultiTeacherNode = true;
      } else {
        baseFeature.properties.allSchoolIDs = null;
        baseFeature.properties.isMultiTeacherNode = false;
      }
      return baseFeature;
    });

    return { ...geoJson, features: combinedFeatures };
  };

  useEffect(() => {
    if (maplibregl && typeof maplibregl.addProtocol === "function") {
      try { const p = new Protocol(); maplibregl.addProtocol("pmtiles", p.tile); } catch (e) {}
    }

    const map = new maplibregl.Map({
      container: mapRef.current,
      style:     "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
      center:    [-98.5795, 39.8283],
      zoom:      3,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    map.on("load", async () => {
      try {
        const rawWishlistData = await fetch("/api/wishlists").then(r => r.json());
        const tweaks = [
          ["water",        "fill-color", "#a8d8ea"],
          ["landcover",    "fill-color", "#eafbe0"],
          ["park",         "fill-color", "#c8ecc0"],
          ["building",     "fill-color", "#ffe8c2"],
          ["road_major",   "line-color", "#ffb703"],
          ["road_minor",   "line-color", "#ffd9a0"],
        ];
          tweaks.forEach(([layerId, prop, val]) => {
          if (map.getLayer(layerId)) map.setPaintProperty(layerId, prop, val);
        });
        // Count total original individual listings
        const count = rawWishlistData.features?.length || 0;
        setWishlistCount(count);
        setTotalCount(count);

        // Pre-process our GeoJSON data to consolidate multi-teacher schools
        const processedData = groupDataByCoordinates(rawWishlistData);
        wishlistDataRef.current = rawWishlistData;

        map.addSource("wishlists", {
          type: "geojson", data: processedData,
          cluster: true, clusterRadius: 40, clusterMaxZoom: 14,
        });
        await Promise.all([
          loadSVGImage(map, "school-pin",       svgStr),
          loadSVGImage(map, "school-pin-multi", svgMultiStr),
        ]);
        // ── Map Styles and Layers ──
        map.addLayer({ id: "wishlist-clusters", type: "circle", source: "wishlists",
          filter: ["has", "point_count"],
          paint: {
            "circle-radius": ["step", ["get", "point_count"], 20, 3, 26, 6, 34],
              "circle-color": [
                "step", ["get", "point_count"],
                "#fb8500",   // small clusters: orange
                3, "#8ecae6", // medium: sky blue
                6, "#ffb703"  // large: sunny yellow
              ],
              "circle-stroke-width": 4,
              "circle-stroke-color": "#ffffff",
            }
        });
        map.addLayer({ id: "wishlist-cluster-count", type: "symbol", source: "wishlists",
          filter: ["has", "point_count"],
          layout: { "text-field": "{point_count_abbreviated}", "text-size": 13, "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"] },
          paint: { "text-color": "#fff" },
        });
        map.addLayer({ id: "wishlist-glow", type: "circle", source: "wishlists",
          filter: ["!", ["has", "point_count"]],
          paint: { "circle-radius": 14, "circle-color": "#f97316", "circle-opacity": 0.2, "circle-blur": 1 },
        });
        map.addLayer({
          id:     "wishlist-points",
          type:   "symbol",
          source: "wishlists",
          filter: ["!", ["has", "point_count"]],
          layout: {
            "icon-image": [
              "case",
              ["boolean", ["get", "isMultiTeacherNode"], false],
              "school-pin-multi",
              "school-pin",
            ],
            "icon-size":         0.0,
            "icon-anchor":       "bottom",
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          },
        });
        let t = 0;
        const grow = () => {
          t += 0.05;
          const size = Math.min(0.9, t) + Math.sin(t * 6) * Math.max(0, 0.15 - t * 0.1); // slight overshoot bounce
          map.setPaintProperty; // n/a — icon-size is layout, so use setLayoutProperty
          map.setLayoutProperty("wishlist-points", "icon-size", Math.max(0, size));
          if (t < 1) requestAnimationFrame(grow);
        };
        requestAnimationFrame(grow);

        if (initialSchoolID) {
          const feature = rawWishlistData.features?.find(
            f => f.properties?.schoolID === initialSchoolID
          );
          if (feature) {
            const [lng, lat] = feature.geometry.coordinates;
            map.flyTo({ center: [lng, lat], zoom: 12 });
            setSingleSchoolID(initialSchoolID);
          }
        }

        // ── Single point / Stacked single-school node click handler ──
        map.on("click", "wishlist-points", (e) => {
          const p = e.features[0].properties;
          if (popupRef.current) popupRef.current.remove();

          if (p.isMultiTeacherNode && p.allSchoolIDs) {
            // 🌟 Point is a compiled single school node containing multiple teachers
            const parsedIDs = JSON.parse(p.allSchoolIDs);
            setSingleRef.current(null);
            setClusterRef.current(parsedIDs); // Instantly open sliding carousel panel
          } else {
            // Regular individual isolated school point
            setClusterRef.current(null);
            setSingleRef.current(p.schoolID);
          }
        });

        // ── Dynamic geographic distance cluster click handler ──
        const handleClusterClick = (e) => {
          const feature   = e.features[0];
          const clusterId = feature.properties.cluster_id;
          const coords    = feature.geometry.coordinates;
          const source    = map.getSource("wishlists");
          if (!source || clusterId == null) return;

          // 1. Zoom the map in smoothly right away (restoring your working zoom behavior)
          map.easeTo({ center: coords, zoom: map.getZoom() + 2 });

          // 2. Safely unpack leaves from MapLibre
          source.getClusterLeaves(clusterId, 100, 0, (err, leaves) => {
            if (err || !leaves?.length) return;

            const aggregatedIDs = [];

            leaves.forEach(leaf => {
              const props = leaf.properties;
              if (!props) return;

              // Handle MapLibre's stringified booleans safely
              const isMulti = props.isMultiTeacherNode === true || props.isMultiTeacherNode === "true";

              if (isMulti && props.allSchoolIDs) {
                try {
                  // If it's a string, parse it; if it's already an array/object, use it
                  const parsed = typeof props.allSchoolIDs === "string"
                    ? JSON.parse(props.allSchoolIDs)
                    : props.allSchoolIDs;

                  if (Array.isArray(parsed)) {
                    aggregatedIDs.push(...parsed);
                  }
                } catch (parseErr) {
                  console.error("Error parsing stacked school IDs:", parseErr);
                }
              } else if (props.schoolID) {
                aggregatedIDs.push(props.schoolID);
              }
            });

            // Clean up duplicates just in case
            const uniqueIDs = Array.from(new Set(aggregatedIDs)).filter(Boolean);

            // 3. Update the panel state with the unpacked IDs
            if (uniqueIDs.length === 1) {
              setSingleRef.current(uniqueIDs[0]);
              setClusterRef.current(null);
            } else if (uniqueIDs.length > 1) {
              setClusterRef.current(uniqueIDs);
              setSingleRef.current(null);
            }
          });
        };

        map.on("click", "wishlist-clusters",      handleClusterClick);
        map.on("click", "wishlist-cluster-count", handleClusterClick);

        ["wishlist-points", "wishlist-clusters", "wishlist-cluster-count"].forEach(layer => {
          map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
          map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
        });
      } catch (err) { console.error("Failed to load wishlist layer:", err); }
    });

    mapObj.current = map;
    return () => map.remove();
  }, []);

  const applyFilters = useCallback(async (f) => {
    setFilters(f);
    const params = new URLSearchParams();
    if (f.minValue    > 0)    params.set("minValue",       f.minValue);
    if (f.maxValue    < 500)  params.set("maxValue",       f.maxValue);
    if (f.minNeeded   > 0)    params.set("minItemsNeeded", f.minNeeded);
    if (f.maxNeeded   < 100)  params.set("maxItemsNeeded", f.maxNeeded);
    if (f.categories?.length) params.set("categories",     f.categories.join(","));
    if (f.states?.length)     params.set("states",         f.states.join(","));
    if (f.grades?.length)     params.set("grades",         f.grades.join(","));
    if (f.itemMinPrice > 0)   params.set("itemMinPrice",   f.itemMinPrice);
    if (f.itemMaxPrice < 200) params.set("itemMaxPrice",   f.itemMaxPrice);
    if (f.minEnrollment > 0)     params.set("minEnrollment", f.minEnrollment);
    if (f.maxEnrollment < 2000)  params.set("maxEnrollment", f.maxEnrollment);
    if (f.minLunchPct > 0)       params.set("minLunchPct",   f.minLunchPct);
    if (f.maxLunchPct < 100)     params.set("maxLunchPct",   f.maxLunchPct);

    const url  = `/api/wishlists${params.toString() ? "?" + params.toString() : ""}`;
    const data = await fetch(url).then(r => r.json());

    wishlistDataRef.current = data;
    setWishlistCount(data.features?.length || 0);

    // Group dynamically after a layout filter query executes
    const filteredAndGrouped = groupDataByCoordinates(data);
    if (mapObj.current?.getSource("wishlists")) {
      mapObj.current.getSource("wishlists").setData(filteredAndGrouped);
    }
  }, []);

  const applyFiltersDebounced = useCallback(debounce(applyFilters, 500), [applyFilters]);

  return (
    <div className="demographics-map-root" style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <div className="mobile-toggle-bar" style={{ padding: "8px 12px", background: "#fffbf5", borderBottom: "2px solid #fde68a", gap: 8, flexShrink: 0 }}>
        <button onClick={() => setFilterOpen(o => !o)} style={{
          flex: 1, padding: "10px", borderRadius: 12,
          border: "2px solid #fde68a", background: filterOpen ? "#1e293b" : "#fff9e6",
          color: filterOpen ? "#fff" : "#92400e", fontWeight: 800, fontSize: 14, cursor: "pointer",
        }}>
          🎯 {filterOpen ? "Hide Filters ▲" : "Show Filters ▼"}
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "row", flex: 1, minHeight: 0, position: "relative" }}>
        <div className={`filter-panel-wrap${filterOpen ? " open" : ""}`}>
          <MapFilterPanel
            onFiltersChange={(f) => applyFiltersDebounced(f)}
            stats={{ totalShown: wishlistCount, totalAll: totalCount }}
          />
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative" }}>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

          {singleSchoolID && !clusterSchoolIDs && (
            <ClusterPanel
              schoolIDs={[singleSchoolID]}
              filters={filters}
              onClose={() => setSingleSchoolID(null)}
              onViewDetail={(id) => { setSingleSchoolID(null); setDetailSchoolID(id); }}
            />
          )}
          {clusterSchoolIDs && (
            <ClusterPanel
              schoolIDs={clusterSchoolIDs}
              filters={filters}
              onClose={() => setClusterSchoolIDs(null)}
              onViewDetail={(id) => { setClusterSchoolIDs(null); setDetailSchoolID(id); }}
            />
          )}
          {detailSchoolID && (
            <WishlistDetailPanel
              schoolID={detailSchoolID}
              itemMinPrice={filters.itemMinPrice}
              itemMaxPrice={filters.itemMaxPrice}
              categories={filters.categories}
              onClose={() => setDetailSchoolID(null)}
            />
          )}
        </div>
      </div>

      <style>{`
        .demographics-map-root {
          height: 700px;
        }
        .mobile-toggle-bar {
          display: none;
        }
        .filter-panel-wrap {
          width: 320px;
          flex-shrink: 0;
          overflow-y: auto;
          z-index: 5;
        }
        @media (max-width: 767px) {
          .demographics-map-root {
            height: 100vh;
          }
          .mobile-toggle-bar {
            display: flex;
          }
          .filter-panel-wrap {
            width: 100%;
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: #fff;
            display: none;
          }
          .filter-panel-wrap.open {
            display: block;
          }
        }
      `}</style>
    </div>
  );
}